/**
 * MD-11 FE-CHAT — chat thread state machine (§4.1)
 *
 * IDLE → SENDING → STREAMING → IDLE                      (happy path)
 *               ↘ REFUSAL_RENDER → IDLE                  (Tier 1/2/3)
 *               ↘ ERROR_RENDER → SENDING                  (retry)
 *
 * PIN_DRIFT_WARNING is an additive state — surfaces alongside STREAMING
 * when backend emits `pin_drift_warning` event. Does not block streaming.
 *
 * Reducer is pure — caller renders banner / message buffer based on
 * returned state. No DOM/React assumption here; this module is testable
 * as a value transformer.
 */

import type { CitationShape, RefusalResponse } from '../refusal/types';
import type { ApiErrorCode, SseEvent } from './sse-types';

export type ChatState =
  | { kind: 'IDLE' }
  | { kind: 'SENDING'; query: string }
  | {
      kind: 'STREAMING';
      buffer: string;
      citation_markers: { cid: string; span_start: number; span_end: number }[];
      citations: CitationShape[];
      pin_drift?: { branch: 2 | 3 | 4; chunk_id: string };
      step?: { step_n: number; total_steps: number };
      progress?: { step: number; total: number; label_vi: string; label_en: string; status: 'done' | 'running' }[];
    }
  | {
      kind: 'REFUSAL_RENDER';
      refusal: RefusalResponse;
    }
  | {
      kind: 'ERROR_RENDER';
      code: ApiErrorCode;
      message: string;
      retry_query: string;
    };

export type ChatAction =
  | { kind: 'submit'; query: string }
  | { kind: 'sse_event'; event: SseEvent }
  | { kind: 'sse_close' }
  | { kind: 'retry' };

export const INITIAL_STATE: ChatState = { kind: 'IDLE' };

function ensureStreaming(state: ChatState): Extract<ChatState, { kind: 'STREAMING' }> {
  if (state.kind === 'STREAMING') return state;
  return { kind: 'STREAMING', buffer: '', citation_markers: [], citations: [] };
}

export function reduceChat(state: ChatState, action: ChatAction): ChatState {
  switch (action.kind) {
    case 'submit':
      return { kind: 'SENDING', query: action.query };

    case 'retry':
      if (state.kind === 'ERROR_RENDER') {
        return { kind: 'SENDING', query: state.retry_query };
      }
      return state;

    case 'sse_close':
      // Normal terminal — only return to IDLE if not in REFUSAL/ERROR
      if (state.kind === 'STREAMING' || state.kind === 'SENDING') {
        return INITIAL_STATE;
      }
      return state;

    case 'sse_event': {
      const event = action.event;
      switch (event.type) {
        case 'token': {
          const stream = ensureStreaming(state);
          return { ...stream, buffer: stream.buffer + event.text };
        }
        case 'citation_marker': {
          const stream = ensureStreaming(state);
          return {
            ...stream,
            citation_markers: [
              ...stream.citation_markers,
              { cid: event.cid, span_start: event.span_start, span_end: event.span_end },
            ],
          };
        }
        case 'citation_meta': {
          const stream = ensureStreaming(state);
          // Build CitationShape stub — span fields filled when caller correlates
          // marker by cid. UI renders meta panel cards even without span info.
          const matchingMarker = stream.citation_markers.find((m) => m.cid === event.cid);
          const citation: CitationShape = {
            cid: event.cid,
            span_start: matchingMarker?.span_start ?? 0,
            span_end: matchingMarker?.span_end ?? 0,
            ref: event.ref,
          };
          return { ...stream, citations: [...stream.citations, citation] };
        }
        case 'pin_drift_warning': {
          const stream = ensureStreaming(state);
          return {
            ...stream,
            pin_drift: { branch: event.branch, chunk_id: event.chunk_id },
          };
        }
        case 'step_complete': {
          const stream = ensureStreaming(state);
          return {
            ...stream,
            step: { step_n: event.step_n, total_steps: event.total_steps },
          };
        }
        case 'progress': {
          const stream = ensureStreaming(state);
          const prev = stream.progress ?? [];
          // Replace same-step entry (running->done transition) else append, keep ordered by step.
          const next = prev.filter((p) => p.step !== event.step);
          next.push({ step: event.step, total: event.total, label_vi: event.label_vi, label_en: event.label_en, status: event.status });
          next.sort((a, b) => a.step - b.step);
          return { ...stream, progress: next };
        }
        case 'sources': {
          // Ask Grounded RAG doc-level sources event. The /chat reducer does
          // not own the doc-level citation panel (that is the /ask route's
          // CitationSourcesPanel); ignore here to keep streaming state intact.
          return state;
        }
        case 'refusal': {
          return { kind: 'REFUSAL_RENDER', refusal: event.payload };
        }
        case 'done': {
          // STREAMING/REFUSAL_RENDER both end at done → IDLE (caller persists
          // turn locally; backend already wrote D1 in same DO txn)
          return INITIAL_STATE;
        }
        case 'error': {
          const retryQuery = state.kind === 'SENDING' ? state.query : '';
          return {
            kind: 'ERROR_RENDER',
            code: event.code,
            message: event.message,
            retry_query: retryQuery,
          };
        }
      }
      // Unknown / future event type: safe no-op, keep current state.
      return state;
    }
  }
}

/**
 * Convenience: run a sequence of actions through reduceChat.
 */
export function runActions(initial: ChatState, actions: ChatAction[]): ChatState {
  let s = initial;
  for (const a of actions) s = reduceChat(s, a);
  return s;
}
